import React from 'react';
import {
    Plus,
    Minus,
    HelpCircle,
} from 'lucide-react';

/* ------------------------------------------------------------------
 * Types
 * ---------------------------------------------------------------- */

export type FAQIcon =
    | 'plus'
    | 'minus'
    | 'help';

export type FAQItem = {
    question: string;
    answer: string; // HTML-safe string (rendered server-side)
};

export type FAQProps = {
    title?: string;
    description?: string;
    items: FAQItem[];
    icon?: FAQIcon;
    dark?: boolean;
    includeSchema?: boolean;
};

/* ------------------------------------------------------------------
 * Icon whitelist
 * ---------------------------------------------------------------- */

const ICON_MAP: Record<FAQIcon, React.ElementType> = {
    plus: Plus,
    minus: Minus,
    help: HelpCircle,
};

/* ------------------------------------------------------------------
 * Component
 * ---------------------------------------------------------------- */

const FAQBlock = ({
                 title = 'Frequently Asked Questions',
                 description,
                 items,
                 icon = 'help',
                 dark,
                 includeSchema = true,
             }: FAQProps) => {
    if (!items?.length) return null;

    const Icon = ICON_MAP[icon];

    const bgClass = dark ? 'bg-gray-900' : 'bg-gray-50';
    const textClass = dark ? 'text-white' : 'text-gray-900';
    const subTextClass = dark ? 'text-gray-300' : 'text-gray-600';
    const borderClass = dark ? 'border-gray-800' : 'border-gray-200';

    return (
        <section
            className={`rounded-xl p-6 mt-10 ${bgClass}`}
            aria-labelledby="faq-heading"
        >
            {/* ------------------------------
               Header
            ------------------------------ */}
            <header className="mb-6">
                <div className="flex items-center gap-2">
                    <Icon className="h-5 w-5 opacity-80" />
                    <h2
                        id="faq-heading"
                        className={`text-lg font-semibold ${textClass}`}
                    >
                        {title}
                    </h2>
                </div>

                {!!description && (
                    <p className={`mt-2 text-sm ${subTextClass}`}>
                        {description}
                    </p>
                )}
            </header>

            {/* ------------------------------
               FAQ List
            ------------------------------ */}
            <div className="divide-y">
                {items.map((item, index) => (
                    <details
                        key={item.question}
                        className={`group py-4 ${borderClass}`}
                    >
                        <summary
                            className={`flex cursor-pointer list-none items-start justify-between gap-4 ${textClass}`}
                        >
                            <span className="text-sm font-medium leading-snug">
                                {item.question}
                            </span>

                            {/* Toggle icons */}
                            <span className="mt-0.5 flex h-4 w-4 flex-shrink-0 items-center justify-center">
                                <Plus className="h-4 w-4 group-open:hidden opacity-70" />
                                <Minus className="hidden h-4 w-4 group-open:block opacity-70" />
                            </span>
                        </summary>

                        <div
                            className={`prose prose-sm mt-3 max-w-none !text-sm ${subTextClass}`}
                            dangerouslySetInnerHTML={{
                                __html: item.answer,
                            }}
                        />
                    </details>
                ))}
            </div>

            {/* ------------------------------
               FAQ Schema (JSON-LD)
            ------------------------------ */}
            {includeSchema && (
                <script
                    type="application/ld+json"
                    dangerouslySetInnerHTML={{
                        __html: JSON.stringify({
                            '@context': 'https://schema.org',
                            '@type': 'FAQPage',
                            mainEntity: items.map((item) => ({
                                '@type': 'Question',
                                name: item.question,
                                acceptedAnswer: {
                                    '@type': 'Answer',
                                    text: item.answer,
                                },
                            })),
                        }),
                    }}
                />
            )}
        </section>
    );
};

export default FAQBlock;
